// src/api/products.ts
import axios from 'axios';
import {
  MLCCProduct, 
  MLCCProductFetchParams, 
  AluminumProduct, 
  AluminumProductFetchParams,
  FBARProduct,
  FBARProductFetchParams,
  InductorProduct,
  InductorProductFetchParams,
  CeramicRFProduct,
  CeramicRFProductFetchParams,
  FerriteBeadProduct,
  FerriteBeadProductFetchParams,
  ConnectorProduct,
  ConnectorProductFetchParams,
  PaginatedResponse
} from '../types/product';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

// 获取 MLCC 产品的函数
export const fetchMLCCProducts = async (params: MLCCProductFetchParams): Promise<PaginatedResponse<MLCCProduct>> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('product_type', 'MLCC');
    
    if (params.current) {
      queryParams.append('page', params.current.toString());
    }
    if (params.pageSize) {
      queryParams.append('page_size', params.pageSize.toString());
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.rated_voltage) {
      queryParams.append('mlcc_rated_voltage', params.rated_voltage);
    }
    if (params.package) {
      queryParams.append('mlcc_package', params.package);
    }
    if (params.material) {
      queryParams.append('mlcc_material', params.material);
    }
    if (params.capacity) {
      queryParams.append('mlcc_capacity', params.capacity);
    }
    if (params.tolerance) {
      queryParams.append('mlcc_tolerance', params.tolerance);
    }
    
    const response = await axios.get(`${API_BASE}/products/?${queryParams.toString()}`);
    
    if (response.data && response.data.products) {
      return {
        count: response.data.total_products || response.data.products.length,
        results: response.data.products
      };
    }
    
    return { count: 0, results: [] };
  } catch (error) {
    console.error('获取MLCC产品数据失败:', error);
    if (axios.isAxiosError(error)) {
      console.error('Axios错误:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
    }
    return { count: 0, results: [] };
  }
};

// 获取 Aluminum 产品的函数
export const fetchAluminumProducts = async (params: AluminumProductFetchParams): Promise<PaginatedResponse<AluminumProduct>> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('product_type', 'Aluminum');
    
    if (params.current) {
      queryParams.append('page', params.current.toString());
    }
    if (params.pageSize) {
      queryParams.append('page_size', params.pageSize.toString());
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.rated_voltage) {
      queryParams.append('aluminum_rated_voltage', params.rated_voltage);
    }
    if (params.size) {
      queryParams.append('aluminum_size', params.size);
    }
    if (params.capacitance) {
      queryParams.append('aluminum_capacitance', params.capacitance);
    }
    
    const response = await axios.get(`${API_BASE}/products/?${queryParams.toString()}`);
    
    if (response.data && response.data.products) {
      return {
        count: response.data.total_products || response.data.products.length,
        results: response.data.products
      };
    }
    
    return { count: 0, results: [] };
  } catch (error) {
    console.error('获取铝电解电容器数据失败:', error);
    if (axios.isAxiosError(error)) {
      console.error('Axios错误:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
    }
    return { count: 0, results: [] };
  }
};

// 获取 FBAR 产品的函数
export const fetchFBARProducts = async (params: FBARProductFetchParams): Promise<PaginatedResponse<FBARProduct>> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('product_type', 'FBAR');
    
    if (params.current) {
      queryParams.append('page', params.current.toString());
    }
    if (params.pageSize) {
      queryParams.append('page_size', params.pageSize.toString());
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.frequency_band) {
      queryParams.append('fbar_frequency_band', params.frequency_band);
    }

    const response = await axios.get(`${API_BASE}/products/?${queryParams.toString()}`);
    
    if (response.data && response.data.products) {
      return {
        count: response.data.total_products || response.data.products.length,
        results: response.data.products
      };
    }
    
    return { count: 0, results: [] };
  } catch (error) {
    console.error('Error fetching FBAR products:', error);
    return { count: 0, results: [] };
  }
};

// 获取 Inductor 产品的函数
export const fetchInductorProducts = async (params: InductorProductFetchParams): Promise<PaginatedResponse<InductorProduct>> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('product_type', 'Inductor');
    
    if (params.current) {
      queryParams.append('page', params.current.toString());
    }
    if (params.pageSize) {
      queryParams.append('page_size', params.pageSize.toString());
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.inductance) {
      queryParams.append('inductor_inductance', params.inductance);
    }
    if (params.size) {
      queryParams.append('inductor_size', params.size);
    }
    if (params.inductance_tolerance) {
      queryParams.append('inductor_inductance_tolerance', params.inductance_tolerance);
    }

    const response = await axios.get(`${API_BASE}/products/?${queryParams.toString()}`);
    
    if (response.data && response.data.products) {
      return {
        count: response.data.total_products || response.data.products.length,
        results: response.data.products
      };
    }
    
    return { count: 0, results: [] };
  } catch (error) {
    console.error('Error fetching Inductor products:', error);
    return { count: 0, results: [] };
  }
};

// 获取 CeramicRF 产品的函数
export const fetchCeramicRFProducts = async (params: CeramicRFProductFetchParams): Promise<PaginatedResponse<CeramicRFProduct>> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('product_type', 'CeramicRF');
    
    if (params.current) {
      queryParams.append('page', params.current.toString());
    }
    if (params.pageSize) {
      queryParams.append('page_size', params.pageSize.toString());
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.application) {
      queryParams.append('ceramic_rf_application', params.application);
    }

    const response = await axios.get(`${API_BASE}/products/?${queryParams.toString()}`);
    
    if (response.data && response.data.products) {
      return {
        count: response.data.total_products || response.data.products.length,
        results: response.data.products
      };
    }
    
    return { count: 0, results: [] };
  } catch (error) {
    console.error('Error fetching CeramicRF products:', error);
    return { count: 0, results: [] };
  }
};

// 获取 FerriteBead 产品的函数
export const fetchFerriteBeadProducts = async (params: FerriteBeadProductFetchParams): Promise<PaginatedResponse<FerriteBeadProduct>> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('product_type', 'FerriteBead');
    
    if (params.current) {
      queryParams.append('page', params.current.toString());
    }
    if (params.pageSize) {
      queryParams.append('page_size', params.pageSize.toString());
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.impedance_100mhz) {
      queryParams.append('ferrite_bead_impedance_100mhz', params.impedance_100mhz);
    }

    const response = await axios.get(`${API_BASE}/products/?${queryParams.toString()}`);
    
    if (response.data && response.data.products) {
      return {
        count: response.data.total_products || response.data.products.length,
        results: response.data.products
      };
    }
    
    return { count: 0, results: [] };
  } catch (error) {
    console.error('Error fetching FerriteBead products:', error);
    return { count: 0, results: [] };
  }
};


// 获取 连接器 产品的函数
export const fetchConnectorProducts = async (params: ConnectorProductFetchParams): Promise<PaginatedResponse<ConnectorProduct>> => {
  try {
    const queryParams = new URLSearchParams();
    const productType = params.product_type || 'ConnectorBTB';
    if (productType === 'ConnectorAuto') {
      // 不设置product_type
    } else {
      queryParams.append('product_type', productType);
    }
    
    if (params.current) {
      queryParams.append('page', params.current.toString());
    }
    if (params.pageSize) {
      queryParams.append('page_size', params.pageSize.toString());
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.connector_type) {
      queryParams.append('connector_connector_type', params.connector_type);
    }
    if (params.contact_pitch) {
      queryParams.append('connector_contact_pitch', params.contact_pitch);
    }
    if (params.mated_height_max) {
      queryParams.append('connector_mated_height_max', params.mated_height_max);
    }
    if (params.rated_current_other) {
      queryParams.append('connector_rated_current_other', params.rated_current_other);
    }
    if (params.positions_No) {
      queryParams.append('connector_positions_No', params.positions_No);
    }
    if (productType === 'ConnectorAuto') {
      queryParams.append('auto_connector', 'true');
    }
    const response = await axios.get(`${API_BASE}/products/?${queryParams.toString()}`);
    
    if (response.data && response.data.products) {
      const products = response.data.products.map((product: ConnectorProduct) => {
        if (!product.specs) {
          console.warn('Product missing specs:', product);
        }
        return product;
      });
      
      return {
        count: response.data.total_products || products.length,
        results: products
      };
    }
    
    return { count: 0, results: [] };
  } catch (error) {
    console.error('Error fetching Connector products:', error);
    return { count: 0, results: [] };
  }
};

// 获取单个产品详情的函数
export const fetchProductDetail = async (partNumber: string, type?: string): Promise<MLCCProduct | AluminumProduct | FBARProduct | InductorProduct | CeramicRFProduct | FerriteBeadProduct | ConnectorProduct | null> => {
  try {
    // 使用搜索 API 获取产品信息
    const searchResponse = await axios.get(`${API_BASE}/products/`, {
      params: {
        part_number: partNumber,
        product_type: type,
        exact_match: true,
        search: partNumber // 添加精确搜索
      }
    });

    if (!searchResponse.data?.products?.length) {
      console.warn(`No product found for part_number: ${partNumber}, type: ${type}`);
      return null;
    }

    // 确保返回的产品类型与请求的类型匹配，并且 part_number 完全匹配
    const product = searchResponse.data.products[0];
    if (type && product.product_type !== type) {
      console.warn(`Product type mismatch: expected ${type}, got ${product.product_type}`);
      return null;
    }

    if (product.part_number !== partNumber) {
      console.warn(`Part number mismatch: expected ${partNumber}, got ${product.part_number}`);
      return null;
    }

    return product;
  } catch (error) {
    console.error('Error fetching product detail:', error);
    return null;
  }
};

// 搜索函数
export const fetchSearchResults = async (search: string): Promise<(MLCCProduct | AluminumProduct | FBARProduct | InductorProduct | CeramicRFProduct | FerriteBeadProduct | ConnectorProduct)[]> => {
  try {
    const response = await axios.get<(MLCCProduct | AluminumProduct | FBARProduct | InductorProduct | CeramicRFProduct | FerriteBeadProduct |ConnectorProduct)[]>(`${API_BASE}/products/`, {
      params: {
        part_number: search,
        product_code: search,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching search results:', error);
    return [];
  }
};

// 添加新的通用搜索函数
export const searchProducts = async (params: {
  part_number?: string;
  product_code?: string;
  product_type?: string;
}): Promise<PaginatedResponse<MLCCProduct | AluminumProduct | FBARProduct | InductorProduct | CeramicRFProduct | FerriteBeadProduct | ConnectorProduct>> => {
  try {
    
    // 构建查询参数
    const queryParams: Record<string, string> = {
      product_type: params.product_type || ''
    };
    
    // 使用 search 参数，后端会同时搜索 part_number 和 product_code
    if (params.part_number) {
      queryParams.search = params.part_number;
    } else if (params.product_code) {
      queryParams.search = params.product_code;
    }
    
    const response = await axios.get(`${API_BASE}/products/`, {
      params: queryParams
    });
    
    if (response.data && response.data.products) {
      return {
        count: response.data.total_products || response.data.products.length,
        results: response.data.products
      };
    } else if (response.data && Array.isArray(response.data)) {
      return {
        count: response.data.length,
        results: response.data
      };
    }
    return { count: 0, results: [] };
  } catch (error) {
    console.error('搜索产品失败:', error);
    if (axios.isAxiosError(error)) {
      console.error('Axios错误:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
    }
    return { count: 0, results: [] };
  }
};